/**
 * The registry this origin was built with.
 *
 * Authority: the two-origin split — the staff and admin planes are served from
 * their own Static Web App behind Entra, the customer entry and questionnaire
 * from the public one.
 *
 * **Why a holder rather than an import.** Every gate below asks the registry
 * "is this path on my plane?" rather than reading a prefix out of the URL,
 * because a gate that reads a prefix is a gate a renamed route turns off in
 * silence. But if those gates *import* a registry, whichever registry they
 * import is in the module graph, and `load: () => import(...)` makes the
 * bundler emit every screen it names. Importing the union to answer a question
 * about planes would ship the staff console to the public origin. So the entry
 * point sets it, once, and the gates read it.
 *
 * **It throws rather than defaulting to empty.** An unset registry would make
 * `isStaffPlanePath` and `isCustomerPlanePath` answer `false` everywhere — MSAL
 * would never engage, the customer OTP gate would never render, and every plane
 * check would silently pass as "not my plane". That is the exact failure this
 * indirection exists to avoid, so it is loud.
 */
import type { RouteRegistry } from './types.ts'

let active: RouteRegistry | null = null

/** Called once, by the origin's entry point, before anything renders. */
export function setActiveRegistry(registry: RouteRegistry): void {
  active = registry
}

export function activeRegistry(): RouteRegistry {
  if (active === null) {
    throw new Error(
      'no active route registry — the origin entry point must call setActiveRegistry() before render',
    )
  }

  return active
}

/** Test-only: drop the registry so a suite can prove the unset case throws. */
export function clearActiveRegistry(): void {
  active = null
}
